import { filterDOMProps } from '@react-aria/utils';
import { type ForwardedRef, type ReactNode, type RefObject } from 'react';
import { mergeProps, useButton, useFocusRing, useHover } from 'react-aria';
import { ButtonContext, type ButtonProps, useContextProps } from 'react-aria-components';

export interface DivButtonProps extends Omit<ButtonProps, 'type' | 'children' | 'className'> {
    children?: ReactNode;
    className?: string;
}

export function DivButton({
    ref,
    ...props
}: DivButtonProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    // @ts-expect-error not happy with the ref, but we know it's a div
    [props, ref] = useContextProps(props, ref, ButtonContext);
    const { buttonProps, isPressed } = useButton(
        { elementType: 'div', ...props },
        ref as RefObject<HTMLDivElement>
    );
    const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);
    const { hoverProps, isHovered } = useHover({
        ...props,
        isDisabled: props.isDisabled,
    });

    return (
        <div
            ref={ref}
            {...filterDOMProps(props)}
            {...mergeProps(buttonProps, focusProps, hoverProps)}
            className={props.className}
            slot={props.slot || undefined}
            data-disabled={props.isDisabled || undefined}
            data-pressed={isPressed || undefined}
            data-hovered={isHovered || undefined}
            data-focused={isFocused || undefined}
            data-focus-visible={isFocusVisible || undefined}
            aria-label={props['aria-label'] || undefined}
        >
            {props.children}
        </div>
    );
}
